/**
 * DeleteObjectCommand
 *
 * Records deleting a SceneObject and all its descendants. Undo recreates
 * the full hierarchy from serialized data; redo deletes again.
 *
 * ## Hierarchy preservation across undo/redo
 *
 * Descendants are recreated in the same order the caller captured them
 * (parents-before-children, matching `getDescendants()` traversal). Each
 * entry's parent is resolved at construction time into a stable **index**
 * into the entries array, not a raw SceneObject ref. On undo we rebuild
 * objects in order and look up each entry's parent against the freshly
 * created set by index, so repeated undo/redo cycles correctly re-nest
 * the original hierarchy rather than flattening everything to the scene
 * root (which was the behavior before this fix).
 *
 * Index 0 is always the top-level deleted object; its parent is
 * **external** to the set and held as a shared identity ref (`this.parentRef`):
 * a sibling command in the same undo bundle (an empty-rig prune, a rig wrap)
 * can delete and recreate that parent, and the shared ref is what keeps this
 * command re-nesting under the LIVE instance. Unbound → scene root.
 *
 * ## The seat
 *
 * Undo puts the object back between the siblings it stood between. A rebuilt
 * node is appended to its parent, and sibling order is flow order to a layout,
 * so an object deleted from the middle of a row would come back last and the
 * row would flow around it there. The command records the place the object held
 * among its parent's children (`index`), read at construction while it stands
 * there, and seats the rebuilt object at it, then asks the layout above to solve
 * again. Deletes made one after the other (a multi-delete builds each command
 * right before it deletes) each record their place in the row as it stood then,
 * so undoing them in reverse order lands every seat. The recording keeps the
 * index; one made before it did replays with the object appended, as it always
 * did.
 *
 * ## Library references
 *
 * A library reference comes back as a reference (`referenceMarks.ts`). The
 * command records it as one, and undo runs its nested load again
 * (`TraceScene._hydrateReference`) after the rebuild. What a reference derives
 * is not the scene's, so the entries beneath one are left out however the caller
 * lists them: `[target, ...getDescendants()]` is what the editors hand over, and
 * the derived nodes in it are not serialized. A reference that will not load
 * again, or whose load throws, does not fail the undo: it stands empty and a
 * warning names it.
 *
 * A node deleted from INSIDE a reference (`chair.glb` under a library piece) is
 * recorded as any node is (it is entry 0, and entry 0 is never derived), and
 * undo puts it back under the reference. `TraceScene.canParentUnder` refuses a
 * new child there; a node coming back to where it stood is not asked. When the
 * reference's own load has brought it back already (the reference's delete was
 * undone first), undo binds the command to that node instead of building a
 * second one.
 *
 * ## Construction-before-delete invariant
 *
 * The command captures `deletedObject.traceScene` during construction.
 * Callers MUST build the command while the subject is still live, then
 * call `traceScene.deleteSceneObject(...)`. If you swap the order, the
 * traceScene field on the disposed object becomes unreliable.
 */

import type { UndoCommand, SerializedCommand } from './UndoCommand';
import type { SceneObjectData } from '../../scenedata/sceneobjectdata';
import type { SceneObject } from '../../core/SceneObject';
import type { TraceScene } from '../../core/TraceScene';
import type { SceneObjectRef } from '../../core/SceneObjectIdentity';
import { registerCommandCodec } from './CommandCodec';
import { seatAmongSiblings } from './ReparentCommand';
import { isDerived, readMarks, restoreReferences, type NodeMarks } from './referenceMarks';
import { invalidateNearestLayout } from '../../behaviors/layout';

const CODEC_TYPE = 'DeleteObject';

interface DeleteObjectPayload {
    /**
     * Path of the top-level deleted object as it existed BEFORE the delete.
     * Used by `fromSerialized` to re-acquire the live ref at replay time so
     * `redo()` can dispose it.
     */
    topLevelPath: string;
    /** FQTP of the external parent (null = scene root). */
    parentPath: string | null;
    /** Where the top-level object stood among its parent's children; absent in a recording made without it. */
    index?: number;
    /** Already in stable-tag form — same shape as internal storage. */
    deletedEntries: Array<{ data: SceneObjectData; parent: 'external' | number; marks?: NodeMarks }>;
}

/**
 * One object in the deleted set. Callers pass live SceneObject refs for
 * both `object` and `parent`; the command reads them once during
 * construction to build an index-based parent map and never again.
 *
 * Entry 0 is the top-level — its `parent` is the external parent of the
 * deleted subtree (may be null for scene root). Entries 1+ have `parent`
 * equal to some earlier entry's `object`.
 */
export interface DeletedObjectEntry {
    /**
     * The SceneObject itself, live at construction time. Used to build a
     * ref → index map so that descendants can declare the correct
     * earlier-entry parent. Not stored after construction.
     */
    object: SceneObject;
    /** Serialized data used to recreate the object on undo. */
    data: SceneObjectData;
    /**
     * Parent at construction time. For entry 0 this is the external
     * parent of the deleted tree (may be `null` = scene root). For
     * entries 1+ this must be the `object` ref of some earlier entry.
     * Ref is only read during construction; stale refs after delete do
     * not matter.
     */
    parent: SceneObject | null;
}

/**
 * Internal stable parent descriptor. One of:
 *   - `'external'`: parent is `this.parent` (or scene root if that is null).
 *   - `number`: parent is `this.deletedEntries[number]` — an earlier entry.
 */
type ResolvedEntryParent = 'external' | number;

/**
 * Internal entry shape: serialized data, stable parent tag, and the
 * shared identity ref. Entry 0 is the top-level deleted object; entries
 * 1+ are descendants. Each ref is shared with other commands targeting the
 * same logical SceneObject via the scene's identity registry.
 */
interface InternalDeletedEntry {
    data: SceneObjectData;
    parent: ResolvedEntryParent;
    identity: SceneObjectRef;
    /** What a rebuild from data cannot put back; absent for an ordinary node. */
    marks?: NodeMarks;
}

/**
 * Internal constructor input — used by both the public constructor and
 * `fromSerialized`. Typed so a new private field is caught at compile time
 * regardless of which entry point populated it.
 */
interface DeleteObjectCommandInit {
    traceScene: TraceScene;
    parentRef: SceneObjectRef | null;
    index?: number;
    deletedEntries: InternalDeletedEntry[];
    topLevelPath: string;
    label: string;
    parentHeld: boolean;
}

export class DeleteObjectCommand implements UndoCommand {
    readonly label: string;
    private traceScene: TraceScene;
    /**
     * External parent of the top-level deleted object, as a shared identity
     * ref (see header) — a bundled sibling command may delete/recreate it.
     */
    private parentRef: SceneObjectRef | null;
    /**
     * The place the top-level object held among its parent's children when
     * the command was built (see the header); undefined when it is not known.
     */
    private index: number | undefined;
    /**
     * How many children the parent had, the deleted object among them, when
     * the command was built; unknown for a replayed recording.
     */
    private siblings: number | undefined;
    /**
     * Entries in parents-before-children order. Each entry owns a shared
     * identity ref: bound after construction (before delete) → unbound on
     * redo (delete) → rebound to a fresh instance on undo (recreate).
     */
    private deletedEntries: InternalDeletedEntry[];

    /**
     * Path of the top-level object captured at construction time (when the
     * object is still live). Used only by `serialize()` — the ref itself
     * becomes stale after delete, but we need the path for replay.
     */
    private topLevelPath: string;

    /**
     * Whether the deleted object stood under `parentRef` when the command was
     * made. Only then does undo rebuild it there unasked; a parent handed in
     * that it did not stand under is asked, as for a new object.
     */
    private parentHeld: boolean;

    constructor(
        deletedObject: SceneObject,
        deletedEntries: DeletedObjectEntry[],
        parent: SceneObject | null,
    ) {
        const registry = deletedObject.traceScene.objectIdentityRegistry;

        // Resolve each entry's parent ref into an index (or 'external')
        // while refs are still live. Walk in order so earlier entries
        // become valid parent targets for later entries.
        //
        // What stands beneath a library reference is derived, not the scene's:
        // it is left out here and comes back when the reference loads again.
        // Entry 0 is the deleted object itself, whatever its parent is.
        const refToIndex = new Map<SceneObject, number>();
        const derived = new Set<SceneObject>();
        const resolved: InternalDeletedEntry[] = [];
        deletedEntries.forEach((entry, i) => {
            if (i > 0 && isDerived(entry.parent, derived)) {
                derived.add(entry.object);
                return;
            }
            const tag = resolveEntryParent(entry.parent, refToIndex);
            refToIndex.set(entry.object, resolved.length);
            const marks = readMarks(entry.object);
            resolved.push({
                data: structuredClone(entry.data),
                parent: tag,
                identity: registry.refFor(entry.object),
                ...(marks ? { marks } : {}),
            });
        });

        this.traceScene = deletedObject.traceScene;
        this.parentRef = parent ? registry.refFor(parent) : null;
        this.index = siblingIndex(deletedObject, parent);
        this.siblings = deletedObject.getParent()?.getChildren().length;
        this.topLevelPath = deletedObject.getPath();
        this.deletedEntries = resolved;
        this.label = `Delete ${deletedObject.name}`;
        this.parentHeld = standsUnder(deletedObject, parent);
    }

    private static _fromInit(init: DeleteObjectCommandInit): DeleteObjectCommand {
        const cmd = Object.create(DeleteObjectCommand.prototype) as DeleteObjectCommand;
        const target = cmd as unknown as DeleteObjectCommandInit & { label: string };
        target.traceScene = init.traceScene;
        target.parentRef = init.parentRef;
        target.index = init.index;
        target.deletedEntries = init.deletedEntries;
        target.topLevelPath = init.topLevelPath;
        target.label = init.label;
        target.parentHeld = init.parentHeld;
        return cmd;
    }

    /**
     * Construct a "pre-redo" instance from serialized data. At replay time
     * the target object still exists in the scene (redo will dispose it).
     * We resolve the live subtree before replay so every entry uses the
     * registry ref already held by sibling commands. Redo unbinds those
     * shared refs and undo rebinds them to the recreated subtree.
     */
    static fromSerialized(scene: TraceScene, payload: DeleteObjectPayload): DeleteObjectCommand {
        const topObj = scene.resolveSceneObject(payload.topLevelPath);
        if (!topObj) {
            throw new Error(`[DeleteObjectCommand] Cannot resolve top-level path "${payload.topLevelPath}"`);
        }
        const parentObj = payload.parentPath ? scene.resolveSceneObject(payload.parentPath) : null;
        if (payload.parentPath && !parentObj) {
            throw new Error(`[DeleteObjectCommand] Cannot resolve parent path "${payload.parentPath}"`);
        }
        // Undo rebuilds the object under this parent without asking whether
        // it may hold it, which holds only for a parent it stands under now.
        if (!standsUnder(topObj, parentObj)) {
            throw new Error(`[DeleteObjectCommand] "${payload.topLevelPath}" does not stand under "${payload.parentPath ?? 'the scene root'}"`);
        }
        const registry = scene.objectIdentityRegistry;
        const liveEntries: Array<SceneObject | null> = [topObj];
        const used = new Set<SceneObject>([topObj]);
        for (let i = 1; i < payload.deletedEntries.length; i++) {
            const entry = payload.deletedEntries[i];
            if (entry.parent === 'external') {
                throw new Error(`[DeleteObjectCommand] Descendant entry ${i} has no parent in the deleted subtree`);
            }
            const live = standingChild(liveEntries[entry.parent], entry.data, used);
            liveEntries.push(live);
            if (live) used.add(live);
        }
        const entries: InternalDeletedEntry[] = payload.deletedEntries.map((e, i) => ({
            data: structuredClone(e.data),
            parent: e.parent,
            identity: liveEntries[i] ? registry.refFor(liveEntries[i]) : registry.allocateUnbound(),
            ...(e.marks ? { marks: structuredClone(e.marks) } : {}),
        }));
        return DeleteObjectCommand._fromInit({
            traceScene: scene,
            parentRef: parentObj ? registry.refFor(parentObj) : null,
            ...(payload.index !== undefined ? { index: payload.index } : {}),
            deletedEntries: entries,
            topLevelPath: payload.topLevelPath,
            label: `Delete ${topObj.name}`,
            parentHeld: true,
        });
    }

    serialize(): SerializedCommand {
        const payload: DeleteObjectPayload = {
            topLevelPath: this.topLevelPath,
            parentPath: this.parentRef?.get()?.getPath() ?? null,
            ...(this.index !== undefined ? { index: this.index } : {}),
            deletedEntries: this.deletedEntries.map(e => ({
                data: structuredClone(e.data),
                parent: e.parent,
                ...(e.marks ? { marks: structuredClone(e.marks) } : {}),
            })),
        };
        return { type: CODEC_TYPE, data: payload };
    }

    /**
     * The shared identity ref of the deleted root: what every other command,
     * and the reference-identity registry, holds the object by.
     * @internal Read by {@link PlacedCopyCommand}, so a copy that replaces this
     * one can answer to the same ref.
     */
    _rootIdentity(): SceneObjectRef {
        return this.deletedEntries[0].identity;
    }

    async undo(): Promise<void> {
        const registry = this.traceScene.objectIdentityRegistry;
        // Recreate in order. Each entry's parent tag is stable across
        // undo/redo cycles, so lookups against `created[idx]` land on
        // the fresh instance from the current recreation pass. After
        // each create, rebind the entry's identity ref so any other
        // commands sharing it see the live instance.
        // Resolve the external parent through the shared ref — a bundled
        // sibling (rig prune/wrap) may have recreated it since construction.
        // A ref still pointing at a DISPOSED node (mis-ordered bundle: our
        // undo ran before the sibling rebind) falls back to the scene root
        // rather than re-nesting under a dead TransformNode.
        const externalParent = liveOrUndefined(this.parentRef?.get());
        // The recorded index is a place among the recorded parent's children.
        // Rebuilt under the root because that parent is gone, the object is
        // appended instead: the index means nothing there.
        const underRecordedParent = this.parentRef === null || externalParent !== undefined;
        // Inside a reference, the reference's own load may have brought the
        // node back already (its delete was undone first): rebind to it.
        if (this.parentHeld && externalParent?.referenceAncestor() && this._rebindStanding(externalParent)) return;
        const created: SceneObject[] = [];
        for (const entry of this.deletedEntries) {
            const resolvedParent: SceneObject | undefined =
                entry.parent === 'external' ? externalParent
                : created[entry.parent];
            // Put back where it stood, which nothing has to permit again: a
            // node deleted inside a reference returns under it. A parent the
            // object never stood under is asked, as for a new object.
            const obj = entry.parent === 'external' && !this.parentHeld
                ? await this.traceScene.createSceneObjectFromData(entry.data, resolvedParent)
                : await this.traceScene._restoreSceneObjectFromData(entry.data, resolvedParent);
            created.push(obj);
            registry.rebind(entry.identity, obj);
            // The deleted object first, before what stands under it: back
            // between the siblings it stood between.
            if (created.length === 1) {
                if (underRecordedParent) seatBack(obj, this.index);
                else invalidateNearestLayout(obj.getParent() ?? obj);
            }
        }
        // A reference comes back as one: marked, and loaded again.
        await restoreReferences(
            this.traceScene,
            created.map((object, i) => ({ object, marks: this.deletedEntries[i].marks })),
            'DeleteObjectCommand',
            this.label,
        );
        // Not a node a second undo removed while the references above loaded.
        const firstObj = liveOrUndefined(created[0]);
        if (firstObj) {
            this.traceScene.select(firstObj);
        }
    }

    /**
     * Rebind every entry to the node standing where it was recorded under
     * `parent`, inside a reference, when the deleted node stands at its seat
     * again: the parent has every child it had, and one of the deleted
     * node's name at its seat. By name, since a reference's load mints its
     * nodes' uids afresh. False, binding nothing, when it does not stand there.
     */
    private _rebindStanding(parent: SceneObject): boolean {
        const children = parent.getChildren();
        const top = this.index === undefined || children.length !== this.siblings ? undefined : children[this.index];
        if (!top || top.name !== this.deletedEntries[0].data.name) return false;
        const used = new Set<SceneObject>([top]);
        const live: Array<SceneObject | null> = [top];
        for (const entry of this.deletedEntries.slice(1)) {
            const found = entry.parent === 'external' ? null : standingChild(live[entry.parent], { name: entry.data.name }, used);
            live.push(found);
            if (found) used.add(found);
        }
        const registry = this.traceScene.objectIdentityRegistry;
        this.deletedEntries.forEach((entry, i) => registry.rebind(entry.identity, live[i]));
        this.traceScene.select(top);
        return true;
    }

    // Async for symmetry with `undo()` — this one could be synchronous
    // today, but declaring both halves as `Promise<void>` prevents a subtle
    // trap where a caller awaiting `cmd.redo()` in one code path silently
    // drops into a synchronous return in another.
    async redo(): Promise<void> {
        const top = this.deletedEntries[0]?.identity.get() ?? null;
        if (top) {
            this.traceScene.deleteSceneObject(top);
        }
        // Unbind every entry. Other commands that share these refs will
        // start no-op'ing at undo/redo time until this command's undo
        // runs and rebinds them.
        const registry = this.traceScene.objectIdentityRegistry;
        for (const entry of this.deletedEntries) {
            registry.rebind(entry.identity, null);
        }
        // Single select call: points at the parent if we have one, else
        // clears selection entirely. Either way, whatever was selected
        // before (which might be the now-disposed object we just deleted)
        // is replaced. Using a single call avoids firing two
        // `selection-changed` events back-to-back for listeners that
        // re-render on selection updates.
        this.traceScene.select(this.parentRef?.get() ?? null);
    }
}

/**
 * The place `object` holds among its parent's children, for the parent the
 * command puts it back under (`parent`, or the scene root when that is null).
 * `undefined` when it does not stand under that parent, so a caller that names
 * another one gets the object appended, as before, rather than a seat among
 * strangers.
 */
function siblingIndex(object: SceneObject, parent: SceneObject | null): number | undefined {
    const actual = object.getParent();
    if (!actual || actual !== (parent ?? object.traceScene.getSceneRoot())) return undefined;
    const index = actual.getChildren().indexOf(object);
    return index >= 0 ? index : undefined;
}

/**
 * Put a rebuilt `object` (appended last) at `index` among its parent's
 * children. Layout does not listen to sibling order, so when the seat moves
 * anything the layout above is asked to solve again. An object that was last,
 * or a seat the parent has no room for now, stays where it is and asks for
 * nothing.
 */
function seatBack(object: SceneObject, index: number | undefined): void {
    const parent = object.getParent();
    if (!parent || index === undefined || index >= parent.getChildren().length - 1) return;
    seatAmongSiblings(parent, object, index);
    invalidateNearestLayout(parent);
}

/** The child of `parent` that `data` describes, by its uid, else its name, and not in `used`. */
function standingChild(
    parent: SceneObject | null | undefined,
    data: Pick<SceneObjectData, 'name' | 'uid'>,
    used: ReadonlySet<SceneObject>,
): SceneObject | null {
    return parent?.getChildren().find(candidate =>
        !used.has(candidate) && (data.uid !== undefined ? candidate.uid === data.uid : candidate.name === data.name),
    ) ?? null;
}

/**
 * Translate a live parent ref into a stable ResolvedEntryParent tag.
 * Unknown refs (not in the map and not null) are treated as external —
 * defensive fallback for entries whose parent isn't in the deleted set.
 */
function resolveEntryParent(
    parentRef: SceneObject | null,
    refToIndex: Map<SceneObject, number>,
): ResolvedEntryParent {
    if (parentRef === null) return 'external';
    const idx = refToIndex.get(parentRef);
    if (idx !== undefined) return idx;
    return 'external';
}

/** Whether `object` stands under `parent` now; `null` names the scene root. */
function standsUnder(object: SceneObject, parent: SceneObject | null): boolean {
    const actual = object.getParent();
    return parent ? actual === parent : actual === null || actual === object.traceScene.getSceneRoot();
}

/** Live (not disposed) ref target, else undefined — see CreateObjectCommand. */
function liveOrUndefined(obj: SceneObject | null | undefined): SceneObject | undefined {
    if (!obj) return undefined;
    const node = (obj as { transform?: { isDisposed?: () => boolean } }).transform;
    if (typeof node?.isDisposed === 'function' && node.isDisposed()) return undefined;
    return obj;
}

registerCommandCodec(CODEC_TYPE, (data, scene): DeleteObjectCommand => {
    return DeleteObjectCommand.fromSerialized(scene, data as DeleteObjectPayload);
});
